import * as React from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

import { useGetSpendAdminSettingsQuery, useUpdateSpendAdminSettingsMutation } from "@/api/spend";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { SpendSettingsUpdate } from "@/types/spend";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const DEFAULT_VALUES: SpendSettingsUpdate = {
  fiscal_start_month: 8,
  default_period_type: "calendar",
  selected_fx_source: "",
  coverage_start_date: "",
  coverage_end_date: "",
};

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const isSettingsRequired = (error: unknown) =>
  (error as { status?: number; data?: { detail?: { code?: string } } })?.status === 422 &&
  (error as { data?: { detail?: { code?: string } } })?.data?.detail?.code === "spend_settings_required";

export const SpendSettingsDialog = ({ open, onOpenChange }: Props) => {
  const { data, isLoading, isError, error } = useGetSpendAdminSettingsQuery(undefined, { skip: !open });
  const settingsRequired = isSettingsRequired(error);
  const [updateSettings, { isLoading: isSaving }] = useUpdateSpendAdminSettingsMutation();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
    getValues,
  } = useForm<SpendSettingsUpdate>({
    defaultValues: DEFAULT_VALUES,
  });

  React.useEffect(() => {
    if (!open) {
      reset(DEFAULT_VALUES);
      return;
    }
    if (isDirty || (isError && !settingsRequired)) return;
    reset(
      data
        ? {
            fiscal_start_month: data.fiscal_start_month,
            default_period_type: data.default_period_type,
            selected_fx_source: data.selected_fx_source,
            coverage_start_date: data.coverage_start_date ?? "",
            coverage_end_date: data.coverage_end_date ?? "",
          }
        : DEFAULT_VALUES,
    );
  }, [data, isDirty, isError, open, reset, settingsRequired]);

  const onSubmit = async (values: SpendSettingsUpdate) => {
    try {
      await updateSettings({ ...values, selected_fx_source: values.selected_fx_source.trim() }).unwrap();
      toast.success("Spend Centre settings saved");
      onOpenChange(false);
    } catch {
      toast.error("Spend Centre settings could not be saved");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Spend Centre settings</DialogTitle>
          <DialogDescription>
            Configure reporting periods and the exchange-rate source for your organization.
          </DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading settings…</p>
        ) : isError && !settingsRequired ? (
          <p role="alert" className="text-sm text-red-700">
            Spend Centre settings could not be loaded.
          </p>
        ) : (
          <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
            <fieldset className="space-y-3">
              <legend className="text-sm font-medium">Spend data coverage</legend>
              <p className="text-xs text-muted-foreground">
                Applied to all active and expiring contract families. The end date is inclusive.
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="spend-coverage-start">Spend data from</Label>
                  <Input
                    id="spend-coverage-start"
                    type="date"
                    aria-label="Spend data from"
                    aria-invalid={Boolean(errors.coverage_start_date)}
                    {...register("coverage_start_date", { required: "Start date is required" })}
                  />
                  {errors.coverage_start_date ? (
                    <p role="alert" className="text-sm text-red-700">
                      {errors.coverage_start_date.message}
                    </p>
                  ) : null}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="spend-coverage-end">Spend data through</Label>
                  <Input
                    id="spend-coverage-end"
                    type="date"
                    aria-label="Spend data through"
                    aria-invalid={Boolean(errors.coverage_end_date)}
                    {...register("coverage_end_date", {
                      required: "End date is required",
                      validate: value =>
                        !getValues("coverage_start_date") || value >= getValues("coverage_start_date")
                          ? true
                          : "End date must be on or after start date",
                    })}
                  />
                  {errors.coverage_end_date ? (
                    <p role="alert" className="text-sm text-red-700">
                      {errors.coverage_end_date.message}
                    </p>
                  ) : null}
                </div>
              </div>
            </fieldset>
            <div className="space-y-2">
              <Label htmlFor="spend-fiscal-month">Fiscal year starts in</Label>
              <select
                id="spend-fiscal-month"
                aria-label="Fiscal year starts in"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                {...register("fiscal_start_month", { valueAsNumber: true })}
              >
                {MONTHS.map((month, index) => (
                  <option key={month} value={index + 1}>
                    {month}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">The fiscal year always starts on Day 1.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="spend-period-type">Default reporting period</Label>
              <select
                id="spend-period-type"
                aria-label="Default reporting period"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                {...register("default_period_type")}
              >
                <option value="calendar">Calendar year</option>
                <option value="fiscal">Fiscal year</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="spend-fx-source">FX source identifier</Label>
              <Input
                id="spend-fx-source"
                aria-label="FX source identifier"
                maxLength={128}
                aria-invalid={Boolean(errors.selected_fx_source)}
                {...register("selected_fx_source", {
                  validate: value => value.trim().length > 0 || "FX source identifier is required",
                })}
              />
              <p className="text-xs text-muted-foreground">The identifier must match exchange_rate.source.</p>
              {errors.selected_fx_source ? (
                <p role="alert" className="text-sm text-red-700">
                  {errors.selected_fx_source.message}
                </p>
              ) : null}
            </div>
            <DialogFooter>
              <Button type="submit" disabled={isSaving}>
                Save Spend Centre settings
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
